import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

import { isStaleBundleError, recoverFromStaleBundle } from "@/core/utils/stale-bundle";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Boundary around every lazy route so a chunk-load failure or render
 * error produces a recoverable message instead of a blank white page.
 * Reloading gets the user out (a fresh navigation re-fetches the chunk).
 *
 * A chunk that 404s usually means the tab is a deploy behind rather than
 * that anything is broken, so that case reloads itself instead of asking
 * the operator to read an error and press a button. The message stays for
 * everything else — and for a chunk that is missing however often we
 * reload, which is a broken deploy the operator should see.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Route error:", error, info.componentStack);
    if (isStaleBundleError(error)) recoverFromStaleBundle();
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center"
        >
          <h2 className="text-lg font-semibold text-slate-900">
            Something went wrong loading this page.
          </h2>
          <p className="max-w-md text-sm text-slate-600">
            {this.state.error.message || "An unexpected error occurred."}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
